import React, { useEffect, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Grid2,
  Tab,
  Tabs,
  Typography,
  Paper,
  Divider,
  alpha,
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  LocationOn,
  PeopleAlt,
  BusinessCenter,
  EventNote,
  VerifiedUser,
  NavigateNext,
  PieChart,
  Description,
} from '@mui/icons-material';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';

// Component Imports
import ViewFacilityDetails from './view-facility/ViewFacilityDetails';
import FacilitySamplePoints from './view-facility/FacilitySamplePoints';
import FacilityPersonnels from './view-facility/FacilityPersonnels';
import FacilityConsultants from './view-facility/FacilityConsultants';
import FacilityScheduler from './view-facility/FacilityScheduler';
import FacilityCertificates from './view-facility/FacilityCertificates';
import { getFacilitiyV2 } from '../../../helpers/api';
import FacilityResults from './view-facility/FacilityResults';
import FacilityGeneratedReports from './view-facility/FacilityGeneratedReports';

const FacilityViewScreen = withRouter(({ props, history, location }) => {
  // 1. Extract the facilityId directly from the URL path
  const currentFacilityId = location.pathname.split('/')[2];

  const searchParams = new URLSearchParams(location.search);
  const currentTab = searchParams.get('tab') || 'samplepoint';

  const [facilityList, setFacilityList] = useState([]);
  const [facilityLoading, setFacilityLoading] = useState(false);
  const [facilitySelected, setFacilitySelected] = useState(null);

  // Tab Configuration
  const tabs = [
    {
      value: 'samplepoint',
      label: 'Sample Points',
      icon: <LocationOn sx={{ fontSize: 18 }} />,
      component: () => <FacilitySamplePoints facilityId={currentFacilityId} />,
    },
    {
      value: 'personnel',
      label: 'Personnel',
      icon: <PeopleAlt sx={{ fontSize: 18 }} />,
      component: () => <FacilityPersonnels facilityId={currentFacilityId} props={props} />,
    },
    {
      value: 'consultant',
      label: 'Consultants',
      icon: <BusinessCenter sx={{ fontSize: 18 }} />,
      component: () => <FacilityConsultants props={props} facilityId={currentFacilityId} />,
    },
    {
      value: 'schedules',
      label: 'Schedules',
      icon: <EventNote sx={{ fontSize: 18 }} />,
      component: () => <FacilityScheduler props={props} facilityId={currentFacilityId} />,
    },
    {
      value: 'results',
      label: 'Results',
      icon: <PieChart sx={{ fontSize: 18 }} />,
      component: () => <FacilityResults props={props} facilityId={currentFacilityId} />,
    },
    {
      value: 'reports',
      label: 'Reports',
      icon: <Description sx={{ fontSize: 18 }} />,
      component: () => <FacilityGeneratedReports facilityId={currentFacilityId} />,
    },
    {
      value: 'certificates',
      label: 'Certificates',
      icon: <VerifiedUser sx={{ fontSize: 18 }} />,
      component: () => <FacilityCertificates props={props} facilityId={currentFacilityId} />,
    },
  ];

  const tabIndex = tabs.findIndex((x) => x.value === currentTab);
  const activeTabIndex = tabIndex !== -1 ? tabIndex : 0;

  // Effect: Handle initial tab redirect if missing from URL
  useEffect(() => {
    if (!searchParams.get('tab')) {
      const newParam = new URLSearchParams(location.search);
      newParam.set('tab', 'samplepoint');
      history.replace({ search: newParam.toString() });
    }
  }, []);

  // Effect: Fetch the facility list once
  useEffect(() => {
    const fetchFacilityList = async () => {
      setFacilityLoading(true);
      try {
        const res = await getFacilitiyV2('', '', '', '', '', '', '', '', 1, 1000);
        const { data, status } = res?.result || {};
        if (status?.code === '00') {
          setFacilityList(data || []);
        }
      } catch (error) {
        console.error('Error fetching facility list:', error);
      } finally {
        setFacilityLoading(false);
      }
    };

    fetchFacilityList();
  }, []);

  // Effect: Keep the selection in sync with the facility in the URL (incl. back/forward)
  useEffect(() => {
    const matchingFacility = facilityList.find(
      (f) => String(f.facilityId) === String(currentFacilityId)
    );
    setFacilitySelected(matchingFacility || null);
  }, [facilityList, currentFacilityId]);

  // Handler: Change Facility via Autocomplete
  const handleFacilityChange = (event, newValue) => {
    if (newValue) {
      setFacilitySelected(newValue);
      // Navigate to the new facility path, preserving the current tab
      history.push(`/facilities/${newValue.facilityId}/view?tab=${currentTab}`);
    }
  };

  const handleTabChange = (event, newValue) => {
    const newParam = new URLSearchParams(location.search);
    newParam.set('tab', tabs[newValue].value);
    history.push({ search: newParam.toString() });
  };

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, bgcolor: '#F4F6F8', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Breadcrumbs separator={<NavigateNext fontSize="small" />}>
          <Link to="/facilities" style={{ textDecoration: 'none' }}>
            <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
              Facilities
            </Typography>
          </Link>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            {facilitySelected ? facilitySelected.facilityName : 'Facility Profile'}
          </Typography>
        </Breadcrumbs>

        <Autocomplete
          disablePortal
          options={facilityList || []}
          loading={facilityLoading}
          value={facilitySelected}
          getOptionLabel={(option) => option?.facilityName || ''}
          isOptionEqualToValue={(option, value) => option.facilityId === value.facilityId}
          onChange={handleFacilityChange}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Switch Facility"
              InputProps={{
                ...params.InputProps,
                sx: { borderRadius: '10px', bgcolor: '#fff' },
                endAdornment: (
                  <React.Fragment>
                    {facilityLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
          size="small"
          sx={{ width: { xs: '100%', sm: '250px' } }}
        />
      </Box>

      <Grid2 container spacing={3} alignItems="stretch">
        {/* LEFT PANEL: Core Details */}
        <Grid2
          size={{ lg: 4, md: 5, sm: 12, xs: 12 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: '20px',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              position: 'sticky',
              top: '20px',
            }}
          >
            <Box sx={{ bgcolor: alpha('#0c5bab', 0.03), p: 1 }}>
              {/* Using currentFacilityId ensures side panel refreshes when Autocomplete changes the URL */}
              <ViewFacilityDetails facilityId={currentFacilityId} />
            </Box>
          </Paper>
        </Grid2>

        {/* RIGHT PANEL: Content Tabs */}
        <Grid2
          size={{ lg: 8, md: 7, sm: 12, xs: 12 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: '20px',
              border: '1px solid',
              borderColor: 'divider',
              minHeight: '75vh',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#fff',
            }}
          >
            <Box sx={{ px: 2, pt: 1 }}>
              <Tabs
                value={activeTabIndex}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    minHeight: 50,
                    color: 'text.secondary',
                    '&.Mui-selected': { color: 'primary.main' },
                  },
                }}
              >
                {tabs.map((tabItem, idx) => (
                  <Tab key={idx} label={tabItem.label} icon={tabItem.icon} iconPosition="start" />
                ))}
              </Tabs>
            </Box>

            <Divider />

            <Box sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>{tabs[activeTabIndex]?.component()}</Box>
          </Paper>
        </Grid2>
      </Grid2>
    </Box>
  );
});

export default FacilityViewScreen;
